import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Agent, AgentType } from '../interfaces/Agent';
import { ResponseObject } from '../interfaces/App';
import { ChatAgent } from '../interfaces/ChatAgent';
import Knowledge from '../interfaces/Knowledge';
import { getAgent } from '../services/agentService';
import { getChatAgents } from '../services/chatAgentService';
import { getKnowledge } from '../services/knowledgeService';

type AgentResult<T extends AgentType> = T extends AgentType.CALL
  ? Agent
  : ChatAgent;

const useGetAgentById = <T extends AgentType = AgentType.CALL>(
  agentType: T = AgentType.CALL as T
) => {
  const { id } = useParams();
  const [agent, setAgent] = useState<AgentResult<T>>();
  const [knowledge, setKnowledge] = useState<Knowledge>();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldNavigate, setShouldNavigate] = useState(false);

  const initializeAgent = async () => {
    if (!id) {
      setShouldNavigate(true);
      setIsLoading(false);
      return;
    }

    let agentData: Agent | ChatAgent | undefined;

    if (agentType === AgentType.CHAT) {
      const chatAgentResponse: ResponseObject = await getChatAgents([id]);
      agentData = chatAgentResponse?.message?.[0];
    } else {
      const agentResponse: ResponseObject = await getAgent(id);
      agentData = agentResponse?.message;
    }

    if (!agentData) {
      setIsLoading(false);
      return;
    }

    setAgent(agentData as AgentResult<T>);

    const knowledgeId = agentData.knowledge_ids?.[0];
    if (knowledgeId) {
      const knowledgeResponse: ResponseObject = await getKnowledge(knowledgeId);
      setKnowledge(knowledgeResponse?.message);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    initializeAgent();
  }, []);

  return {
    agent,
    knowledge,
    isLoading,
    shouldNavigate,
    refetch: initializeAgent,
  };
};

export default useGetAgentById;
